import { getProp, setProp } from '@shared/helpers/operators';
import BulkRecoverModal from '@shared/components/modals/bulk-recover-modal/bulk-recover-modal.vue';
import BulkMoveToTrashModal from '@shared/components/modals/bulk-move-to-trash-modal/bulk-move-to-trash-modal.vue';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import EntityTableProvider from '@shared/components/tables/entity-table-provider/entity-table-provider.vue';
import EntityTable from '@shared/components/tables/entity-table/entity-table.vue';
import BpcPageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { ValidationObserver } from 'vee-validate';
import { mixins } from 'vue-class-component';
import AbstractTable from '@shared/components/tables/abstract-table/abstract-table';
import StdFilter from '@shared/components/filters/std-filter/std-filter.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSync, faPowerOff, faToggleOff, faToggleOn } from '@fortawesome/free-solid-svg-icons';
import DrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import SessionsWeeklyCalendar from '@activities/components/calendars/sessions-weekly-calendar.vue';
import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider';
import { v4 as uuidv4 } from 'uuid';
import BulkEditModal from '@shared/components/modals/bulk-edit-modal/bulk-edit-modal.vue';
import ActivitySessionGameForm from '@activities/components/forms/activity-session-game-form/activity-session-game-form.vue';
import moment from 'moment';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { Activity } from '@activities/interfaces/activity';
import AttendeeTableInput from '@shared/components/forms/inputs/attendee-table-input/attendee-table-input.vue';
import GameSummaryCard from '@activities/components/cards/game-summary-card/game-summary-card.vue';
import GameTripSummaryCard from '@activities/components/cards/game-trip-summary-card/game-trip-summary-card.vue';
import NotifySummonsAttendeeModal from '@activities/components/modals/notify-summons-attendee-modal/notify-summons-attendee-modal.vue';
import CustomFilterSelector from '@shared/components/filters/custom-filter-selector/custom-filter-selector.vue';
import ExtendedFilter from '@shared/components/filters/extended-filter/extended-filter.vue';
import { ActivitySession } from '@activities/interfaces/activity-session';
import ResyncFinddocProductModal from '@access-control/components/modals/resync-finddoc-product-modal/resync-finddoc-product-modal.vue';

library.add(faPowerOff, faToggleOff, faToggleOn, faSync);

@Component({
  name: 'bpc-activity-session-table',
  components: {
    'bpc-entity-table-provider': EntityTableProvider,
    'bpc-entity-table': EntityTable,
    'bpc-page-title': BpcPageTitle,
    'font-awesome-icon': FontAwesomeIcon,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-std-form': StdForm,
    'bpc-std-filter': StdFilter,
    ValidationObserver,
    'bpc-drawer-modal': DrawerModal,
    'bpc-bulk-move-to-trash-modal': BulkMoveToTrashModal,
    'bpc-bulk-recover-modal': BulkRecoverModal,
    'bpc-sessions-weekly-calendar': SessionsWeeklyCalendar,
    'bpc-bulk-edit-modal': BulkEditModal,
    'bpc-activity-session-game-form': ActivitySessionGameForm,
    'bpc-attendee-table-input': AttendeeTableInput,
    'bpc-game-summary-card': GameSummaryCard,
    'bpc-game-trip-summary-card': GameTripSummaryCard,
    'bpc-notify-summons-attendee-modal': NotifySummonsAttendeeModal,
    'bpc-custom-filter-selector': CustomFilterSelector,
    'bpc-extended-filter': ExtendedFilter,
    'bpc-resync-finddoc-product-modal': ResyncFinddocProductModal,
  },
})
export default class ActivitySessionTable extends mixins(AbstractTable) {
  declare $refs: AbstractTable['$refs'] & {
    // resourceForm: InstanceType<typeof ResourceForm> & { saveEntity(): void };
    sessionsWeeklyCalendar: InstanceType<typeof SessionsWeeklyCalendar> & {
      getAllSessionsOfTheWeek: () => Promise<void>;
    };
    stdCancelForm: InstanceType<typeof StdForm> & {
      formModel: any;
      formProvider: EntityFormProvider;
    };
    gameDetailForm: InstanceType<typeof ActivitySessionGameForm> & {
      formModel: any;
      formProvider: EntityFormProvider;
      onSaveGameSession(): Promise<BPCGenericApiResponse>;
    };
    bulkEditModal: InstanceType<typeof BulkEditModal> & {
      saveBulkEdit(): void;
    };
    tableInputGameAttendees: InstanceType<typeof AttendeeTableInput> & {
      getEntities(): void;
    };
    tableInputComingAttendees: InstanceType<typeof AttendeeTableInput> & {
      getEntities(): void;
    };
  };
  @Prop({ type: String, default: 'cancel-activity-session' }) readonly cancelFormConfigName: string;
  @Prop({ type: String, default: 'edit' }) readonly editFormConfigName: string;
  @Prop({ type: String, default: 'calendar' }) readonly typeOfDisplay: string;
  @Prop(Object) readonly activity: Activity;
  @Prop({ type: Array, default: () => [''] })
  declare readonly filtersExcluded: string[];
  @Prop({ type: Boolean, default: false }) readonly showTabFilters: boolean;
  @Prop({ type: Number, default: 1 }) readonly tableKey: number;
  @Prop({ type: Boolean, default: true }) readonly showCalendar: boolean;
  @Prop(Array) readonly initialFilters: Record<string, any>[];
  @Prop({ type: Boolean, default: true }) readonly showTableHeader: boolean;
  @Prop({ type: Boolean, default: true }) readonly withSearch: boolean;
  @Prop({ type: Boolean, default: true }) readonly withFilters: boolean;
  @Prop({ type: Boolean, default: true }) readonly showPagination: boolean;
  @Prop({ type: Boolean, default: true }) readonly showOrderBy: boolean;
  @Prop({ type: Boolean, default: true }) readonly showExtendedFilters: boolean;
  @Prop({ type: Object }) readonly externalFormModel: any;

  public entity: ActivitySession = null;
  public entityName = 'activity-session';
  public pluralEntityName = 'activity-sessions';
  public permissionEntityNameToUse = 'activity_sessions';
  public selectedRowEntity = null;
  public isCancelingEntity: boolean = false;

  public flagsToCheckBeforeLoadEntities: string[] = [
    'configLoaded',
    'filtersLoaded',
    'paginationLoaded',
    'orderLoaded',
    'urlParametersLoaded',
  ];

  //DATA
  public showTeamFilter = false;
  timestamp = moment().unix();
  //Bulk trash modal data
  calendarView = true;
  tableView = false;
  showBulkMoveToTrashModal = false;
  //Bulk recover modal data
  showBulkRecoverModal = false;
  // Bulk cancel modal data
  public isProcessing: boolean = false;
  public eventsSuccess = [];
  public eventsError = [];
  public requestSent: boolean = false;
  public processAll: boolean = false;
  public reportPerPage: number = 20;
  public reportCurrentPage: number = 1;
  dataToShowInBulkReports = [['code', 'name', 'date']];
  sendEmailOptionSelected: boolean = this.defaultSendBookingEventChangeEmail;
  public initializedFormModel = null;
  // Bulk edit modal
  showBulkEditModal = false;
  bulkEditDataToShow = [['code']];
  extraFieldsToBulkEdit: object = { defaultValueSwitchModel: {} };
  extraDataToBulkEdit: object = {};
  showNotifySummonModal: boolean = false;
  refreshTiles: string = uuidv4();
  cancelSessionModalId: string = uuidv4();
  // Filter tab index
  public filterTabIndex: number = 0;
  showResyncFinddocModal: boolean = false;
  resyncAlertKey: number = 0;

  public customPageActions = {
    bulkResyncFinddoc: {
      activated: false,
      type: 'action',
      icon: 'sync',
      variant: 'info',
      text: 'resync',
      event: 'bulk-resync-finddoc',
      selectElements: true,
      tag: 'right',
      permissions: ['update'],
      show: false,
    },
    create: {
      activated: true,
      type: 'action',
      icon: 'plus',
      variant: 'success',
      text: 'create-new',
      show: true,
      event: 'create',
      selectElements: false,
      tag: 'right',
      permissions: ['store'],
    },
    bulkCancel: {
      activated: false,
      type: 'action',
      icon: 'ban',
      variant: 'danger',
      text: 'cancel',
      event: 'bulk-cancel',
      selectElements: true,
      tag: 'left',
      permissions: ['bulk_cancel'],
      show: false,
    },
  };
  public pageActionPositions = ['left', 'bulk', 'middle', 'right'];

  public customRowActions = {
    resyncFinddoc: {
      activated: false,
      type: 'action',
      icon: 'sync',
      show: false,
      event: 'resync-finddoc',
      text: 'resync_finddoc_session',
      permissions: ['update'],
    },
  };

  get rowActions() {
    return {
      ...this.customRowActions,
      ...this.defaultRowActions,
    };
  }

  get defaultSendBookingEventChangeEmail(): boolean {
    return !!this.$store.getters['centerSettings/centerSettings']?.admin_send_session_updated_email;
  }

  get showSendEmailOption(): boolean {
    let showModalUpdate = false;
    // Check if the date of time_range has been changed
    if (this.initializedFormModel && this.$refs.stdForm?.formModel) {
      if (
        JSON.stringify(this.initializedFormModel.time_range) !==
        JSON.stringify(this.$refs.stdForm?.formModel.time_range)
      ) {
        showModalUpdate = true;
      }
      if (this.initializedFormModel.date !== this.$refs.stdForm?.formModel.date) {
        showModalUpdate = true;
      }
    }
    return (
      this.$store.getters['centerSettings/centerSettings']?.send_session_updated_email &&
      showModalUpdate
    );
  }

  get customFormConfigName() {
    return this.entity?.id ? this.editFormConfigName : this.formConfigName;
  }
  get disableChangeView(): boolean {
    return this.tenantType !== 'club' ? true : false;
  }

  //METHODS
  async onSaveGameSession(): Promise<BPCGenericApiResponse> {
    return await this.$refs.gameDetailForm.onSaveGameSession();
  }

  public getActionsByTag(tag: string) {
    const actions = {};
    if (this.pageActions) {
      for (const key of Object.entries(this.pageActions)) {
        if (this.pageActions[key[0]].tag == tag) {
          actions[key[0]] = this.pageActions[key[0]];
        }
      }
    }
    return actions;
  }

  get isProcessingResyncFailed(): boolean {
    return this.$store.getters['activitySessionResyncStore/isProcessingResyncFailed'];
  }
  get isProcessingResyncEnded(): boolean {
    return this.$store.getters['activitySessionResyncStore/isProcessingResyncEnded'];
  }
  get totalCount(): number {
    return this.$store.getters['activitySessionResyncStore/totalCount'];
  }
  get processedCount(): number {
    return this.$store.getters['activitySessionResyncStore/processedCount'];
  }
  get isProcessingResyncRunning(): boolean {
    return this.$store.getters['activitySessionResyncStore/isProcessingResyncRunning'];
  }
  get isResyncClosed(): boolean {
    return this.$store.getters['activitySessionResyncStore/isResyncClosed'];
  }

  onCloseResyncFinddocModal(reload = true) {
    this.showResyncFinddocModal = false;
    if (reload) {
      this.getEntities();
    }
  }

  closeResyncProgressBar() {
    if (this.isProcessingResyncEnded) {
      this.$store.dispatch('activitySessionResyncStore/closeResyncProccess');
    }
  }

  onStartProcessingResync() {
    this.resyncAlertKey = this.resyncAlertKey + 1;
    this.$store.dispatch('activitySessionResyncStore/startResyncProcess');
    this.onCloseResyncFinddocModal(false);
    // If the edit form is open, close it
    if (this.showDrawerSection) {
      this.showDrawerSection = false;
    }
  }

  get resyncAlertStatus(): string {
    if (this.isProcessingResyncRunning) {
      return 'warning';
    } else if (this.isProcessingResyncEnded) {
      return 'success';
    } else if (this.isProcessingResyncFailed) {
      return 'danger';
    } else {
      return 'info';
    }
  }

  listenToWebsocketResyncChannel(): void {
    this.$bpcEcho
      .listenResyncActivitySessionsChannel()
      .listen('.BPC\\Activities\\Events\\ActivitySessionResyncStarted', data => {
        this.$store.dispatch('activitySessionResyncStore/updateResyncStatus', {
          running: true,
          ended: false,
          failed: false,
          totalCount: data.totalCount || 0,
          processedCount: 0,
        });
      })
      .listen('.BPC\\Activities\\Events\\ActivitySessionResyncProcessed', data => {
        this.$store.dispatch('activitySessionResyncStore/updateResyncStatus', {
          running: true,
          ended: false,
          failed: false,
          totalCount: data.totalCount || 0,
          processedCount: data.processedCount || 0,
          isResyncClosed: false,
        });
      })
      .listen('.BPC\\Activities\\Events\\ActivitySessionResyncEnded', data => {
        this.$store.dispatch('activitySessionResyncStore/updateResyncStatus', {
          running: false,
          ended: true,
          failed: false,
          totalCount: data.totalCount || 0,
          processedCount: data.processedCount || 0,
          isResyncClosed: false,
        });
        this.getEntities();
      });
  }

  leaveWebsocketResyncChannel(): void {
    this.$bpcEcho.leaveResyncActivitySessionsChannel();
  }

  mounted() {
    if (this.accessControlActivated && this.useFinddoc) {
      this.listenToWebsocketResyncChannel();
    }
  }

  destroyed() {
    if (this.accessControlActivated && this.useFinddoc) {
      if (!this.isProcessingResyncRunning) {
        this.leaveWebsocketResyncChannel();
      }
    }
  }

  // Bulk soft delete modal (move to trash)
  onBulkSoftDelete() {
    this.showBulkMoveToTrashModal = true;
  }
  onCLoseBulkSoftDelete() {
    this.showBulkMoveToTrashModal = false;
  }
  onSuccessSoftDelete() {
    this.$refs.softDeleteConfirmationModal.onCloseModal();
    if (this.tableView) {
      this.$refs.provider.getEntities();
    }
  }

  getEntities() {
    this.$refs.provider?.getEntities();
  }

  // Bulk restore modal (recover)
  onBulkRecover() {
    this.showBulkRecoverModal = true;
  }
  onCLoseBulkRecover() {
    this.showBulkRecoverModal = false;
  }
  onSuccessBulkRecover() {
    if (this.tableView) {
      this.$refs.provider.getEntities();
    }
  }

  // Bulk edit modal
  onBulkEdit() {
    if (!this.emitOnBulkEdit) {
      this.showBulkEditModal = true;
    } else {
      this.$emit('on-bulk-edit', this.selection);
    }
  }
  onCloseBulkEdit() {
    this.showBulkEditModal = false;
  }
  onFinishBulkEdit() {
    this.$refs.provider.getEntities();
  }

  // Page actions
  customOnPageAction($event: string) {
    switch ($event) {
      case 'bulk-cancel':
        this.onOpenBulkCancelModal();
        break;
      case 'bulk-resync-finddoc':
        this.onOpenBulkResyncFinddocActivitySessionModal();
        break;
    }
  }

  extendUpdatePageActions() {
    if (this.config) {
      for (const activatedPageAction of this.config.data.activatedPageActions) {
        if (this.customPageActions[activatedPageAction]) {
          this.customPageActions[activatedPageAction].activated = true;
        }
      }
    }

    this.customPageActions.bulkCancel.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      this.$refs.provider.queryConfig.params.status !== 'cancelled' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed;

    this.customPageActions.bulkResyncFinddoc.show =
      this.tableIsNotFilteredByTrashedElements() && this.showResyncFinddocButton;
  }

  // Row actions
  customOnRowAction($event: { event: string; item: any }) {
    switch ($event.event) {
      case 'cancel':
        this.onOpenCancelModal($event.item.item);
        break;
      case 'resync-finddoc':
        this.onOpenResyncFinddocActivitySessionModal($event.item.item);
        break;
    }
  }

  onOpenResyncFinddocActivitySessionModal(entity: any) {
    this.selection = [entity.id];
    this.showResyncFinddocModal = true;
  }

  onOpenBulkResyncFinddocActivitySessionModal() {
    this.showResyncFinddocModal = true;
  }

  get accessControlActivated() {
    return this.$modulesService.hasModuleActivated('access_control');
  }

  get useFinddoc(): boolean {
    return (
      this.$store.getters['centerSettings/accessControlConfig']?.access_control_service ===
      'finddoc'
    );
  }

  get showResyncFinddocButton() {
    return (
      this.accessControlActivated &&
      this.useFinddoc &&
      this.$authorizationService.hasPermission('manage_access_control_badges')
    );
  }

  extendUpdateRowActions(entity: any) {
    entity.actions.cancel.show =
      this.tableIsNotFilteredByTrashedElements() && entity.can_be_cancelled;
    entity.actions.edit.show = this.tableIsNotFilteredByTrashedElements() && entity.can_be_edited;
    entity.actions.softDelete.show =
      this.tableIsNotFilteredByTrashedElements() && entity.can_be_trashed;
    entity.actions.resyncFinddoc.show =
      this.tableIsNotFilteredByTrashedElements() &&
      this.showResyncFinddocButton &&
      entity.status !== 'cancelled';
    return null;
  }

  onEditSessionFromCalendar(e) {
    const activity = getProp(e, 'extendedProps.activity');
    this.$emit('edit-from-calendar', activity);
    this.$nextTick().then(() => {
      this.onEditSession({ item: e }, true);
    });
  }

  onEdit(e, disabled = false) {
    if (this.tableView) {
      const activity = getProp(e, 'item.activity');
      this.$emit('edit-from-table', activity);
    }
    this.$nextTick().then(() => {
      this.onEditSession(e, disabled);
    });
  }

  onEditSession(e, disabled = false) {
    if (!this.emitOnEdit) {
      this.entity = e.item;
      this.showForm = true;
      this.showDrawerSection = true;
      setTimeout(() => {
        this.disabledForm = disabled;
      });
    } else {
      this.$emit('on-edit', e);
    }
  }

  presetVariables = {};

  onDateSelectedFromCalendar(e) {
    this.presetVariables = e;
    this.$emit('create-from-calendar');
    this.$nextTick().then(() => {
      this.onNew();
    });
  }

  onModelLoaded() {
    const formModel = this.$refs.stdForm.formModel;
    this.initializedFormModel = JSON.parse(JSON.stringify(this.$refs.stdForm.formModel));
    const newModel = {
      ...formModel,
      ...this.presetVariables,
    };
    this.$set(this.$refs.stdForm, 'formModel', newModel);
    this.presetVariables = {};
    this.$emit('model-loaded');
  }

  setStdFormData(index, value) {
    setProp(this.$refs.stdForm.formModel, index, value);
  }

  changeView(view: string) {
    this.cleanUrlParams();

    this.$nextTick().then(() => {
      if (view === 'table') {
        this.calendarView = false;
        this.tableView = true;
        switch (this.filterTabIndex) {
          case 0:
            this.$emit('load-default-sessions');
            break;
          case 1:
            if (this.showCalendar) {
              this.$emit('load-default-sessions');
            } else {
              this.$emit('load-past-sessions');
            }
            break;
          case 2:
            if (this.showCalendar) {
              this.$emit('load-past-sessions');
            } else {
              this.$emit('load-upcoming-sessions');
            }
            break;
          case 3:
            this.$emit('load-upcoming-sessions');
            break;
          default:
            break;
        }
      }

      if (view === 'calendar') {
        this.tableView = false;
        this.calendarView = true;
        this.filterTabIndex = null;
      }
    });
  }

  async queryParamsChangedFromCalendar(params) {
    if (this.calendarView) {
      await this.$router.push({ query: params }).catch(e => {
        return null;
      });
    }
  }

  cleanUrlParams() {
    this.$router.push({ query: {} }).catch(e => {
      return null;
    });
  }

  onFormSave() {
    if (this.showSendEmailOption) {
      this.$bvModal.show(`confirmation-update-modal-session`);
    } else {
      this.$refs.stdForm.saveEntity();
    }
  }

  actionAfterPrepareToSave(entity: any): AnyEntity {
    if (
      this.customFormConfigName === 'create-for-clubs' ||
      this.customFormConfigName === 'edit-from-management-for-clubs'
    ) {
      entity.activity_group_id = entity.activity?.club_activity_group_id;
      entity.activityGroup = entity.activity?.club_activity_group_id;
    }
    if (this.entityId && this.editFormConfigName === 'edit-from-management-for-clubs') {
      entity.activity_group_id = entity.activity?.activity_groups[0]?.id;
      entity.activityGroup = entity.activity?.activity_groups[0]?.id;
    }
    if (entity.activity?.activity_type === 'competition') {
      entity.type = 'game';
    }
    return entity;
  }

  onFormSaved() {
    if (this.nextAction === 'new') {
      this.setEntityAttributesAfterSave();
      if (this.tableView) {
        this.$refs.provider.getEntities();
      }
    } else {
      this.onFormClose();
      if (this.calendarView) {
        this.$refs.sessionsWeeklyCalendar.getAllSessionsOfTheWeek();
      }
      if (this.tableView) {
        this.$refs.provider.getEntities();
      }
    }
  }

  refreshCalendar() {
    if (this.calendarView) {
      this.$refs.sessionsWeeklyCalendar.getAllSessionsOfTheWeek();
    }
  }

  onOpenCancelModal(entity) {
    this.selectedRowEntity = entity;
    this.$bvModal.show(this.cancelSessionModalId);
  }

  onCloseCancelModal() {
    this.selectedRowEntity = null;
    this.isCancelingEntity = false;
    this.$bvModal.hide(this.cancelSessionModalId);
  }

  onOpenBulkCancelModal() {
    // TODO if only one is selected, call onOpenCancelModal with the selected entity
    this.resetBulkCancelModal();
    this.$bvModal.show('bulk-cancel-modal');
  }

  onCloseBulkCancelModal() {
    this.resetBulkCancelModal();
    this.$bvModal.hide('bulk-cancel-modal');
  }

  resetBulkCancelModal() {
    this.requestSent = false;
    this.isProcessing = false;
    this.eventsSuccess = [];
    this.eventsError = [];
    this.processAll = false;
  }

  onSuccessBulkCancel() {
    if (this.tableView) {
      this.$refs.provider.getEntities();
    }
    if (this.calendarView) {
      this.refreshCalendar();
    }
  }

  onConfirmCancelOne() {
    this.isCancelingEntity = true;
    const basePath = this.$bpcApiRouteHelper.getEntityPath('activity-sessions');
    const relativePath = '/' + this.selectedRowEntity.id + '/cancel';
    this.$bpcApi
      .post(basePath + relativePath, {
        id: this.selectedRowEntity.id,
        cancel_reason: this.$refs.stdCancelForm.formModel['cancel_reason'],
        cancel_reason_description: this.$refs.stdCancelForm.formModel['cancel_reason_description'],
        cancel_orders: this.$refs.stdCancelForm.formModel['cancel_orders'],
        notify_by_email:
          'notify_by_email' in this.$refs.stdCancelForm.formModel
            ? this.$refs.stdCancelForm.formModel['notify_by_email']
            : true,
      })
      .subscribe(
        res => {
          this.$bpcMessage.showSuccessMessage({ text: 'global.generic.successful_action' });
          if (this.tableView) {
            this.$refs.provider.getEntities();
          }
        },
        error => {
          this.$bpcMessage.showErrorMessage({
            text: 'global.generic.error_form_action',
          });
        },
        () => {
          this.$bvModal.hide(this.cancelSessionModalId);
          this.selectedRowEntity = null;
          this.isCancelingEntity = false;
          this.onFormClose();
          this.refreshCalendar();
        },
      );
  }

  onConfirmCancelBulk() {
    const basePath = this.$bpcApiRouteHelper.getEntityPath('activity-sessions');
    const relativePath = '/bulk-cancel';
    const sessionCode = this.generateSessionCode();
    this.listenToWebsocketCancelChannel(sessionCode);
    this.requestSent = true;

    this.$bpcApi
      .post(basePath + relativePath, {
        ids: this.selection,
        filters: this.filterModel,
        search_and_order: this.searchAndOrderQueryParams,
        process_all: this.processAll,
        session_code: sessionCode,
        cancel_reason: this.$refs.stdCancelForm.formModel['cancel_reason'],
        cancel_reason_description: this.$refs.stdCancelForm.formModel['cancel_reason_description'],
        cancel_orders: this.$refs.stdCancelForm.formModel['cancel_orders'],
        notify_by_email:
          'notify_by_email' in this.$refs.stdCancelForm.formModel
            ? this.$refs.stdCancelForm.formModel['notify_by_email']
            : true,
        // ...this.extraData,
      })
      .subscribe(
        res => {
          // this.$emit('request-sent');
        },
        error => {
          // this.$emit('error-sending-request');
          console.log(error);
          this.$bpcMessage.showErrorMessage({
            text: 'global.generic.error_form_action',
          });
          this.$bpcEcho.leaveBulkCancelChannel(sessionCode);
          this.resetBulkCancelModal();
        },
      );
  }

  listenToWebsocketCancelChannel(sessionCode) {
    this.isProcessing = true;
    this.$bpcEcho
      .listenBulkCancelChannel(sessionCode)
      .listen('.BPC\\Core\\Events\\EntitiesCancelled', data => {
        if (data.result?.entities_processed?.length) {
          this.$bpcMessage.showInfoMessage({
            text: 'global.actions.bulk_cancel.entities_cancelled',
          });
        }
        this.isProcessing = false;
        this.onSuccessBulkCancel();
        this.$bpcEcho.leaveBulkCancelChannel(sessionCode);
      })
      .listen('.BPC\\Core\\Events\\EntityCancelled', data => {
        this.eventsSuccess.push(data);
      })
      .listen('.BPC\\Core\\Events\\ErrorCancellingEntity', data => {
        this.eventsError.push(data);
      });
  }

  generateSessionCode(): string {
    return `cancel-${this.tenantId}-${uuidv4()}`;
  }

  onCreateAttendee() {
    this.$root.$emit('triggerActionInEntityTableInput', {
      fieldName: 'activitySessionAttendees',
      action: 'create',
    });
  }

  //COMPUTED
  get tenantId() {
    return this.$store.getters['tenant/selectedTenantId'];
  }

  get tenantType(): string {
    return this.$store.getters['centerSettings/centerSettings']['center_type'] || 'center';
  }

  get activityId(): number {
    return this.activity?.id;
  }

  get pageActions() {
    // Invert the order
    return {
      ...this.customPageActions,
      ...this.defaultPageActions,
    };
  }

  get reportRows() {
    return this.eventsError.length;
  }
  get reportCurrentList() {
    const start = this.reportCurrentPage * this.reportPerPage - this.reportPerPage;
    const end = start + this.reportPerPage;
    return this.eventsError.slice(start, end);
  }

  public get confirmationUpdateModalTitle() {
    return this.$t('global.modals.update-session.title');
  }

  public get confirmButtonText() {
    return this.$t('global.buttons.confirm');
  }

  public get cancelButtonText() {
    return this.$t('global.buttons.cancel');
  }

  public get sendEmailOptionsLabel() {
    return this.$t('global.modals.update-booking-event.send_email.label');
  }

  get activityType(): string {
    return (
      this.activity?.activity_type || getProp(this.entity, 'extendedProps.activity.activity_type')
    );
  }

  get entityId(): number | string {
    return this.entity ? this.entity.id : null;
  }

  confirmUpdateEvent() {
    this.$set(this.$refs.stdForm.formModel, 'notify_by_email', this.sendEmailOptionSelected);
    this.$refs.stdForm.saveEntity();
  }

  // Add summon modal
  onOpenSummonModal() {
    this.showNotifySummonModal = true;
  }

  onCloseAddSummonModal() {
    this.showNotifySummonModal = false;
  }

  refreshSummonsData() {
    this.refreshTiles = uuidv4();
    this.$refs.tableInputGameAttendees?.getEntities();
    this.$refs.tableInputComingAttendees?.getEntities();
  }

  /* Lifecycle hooks */

  // created() {}
  created() {
    if (this.typeOfDisplay === 'table') {
      this.tableView = true;
      this.calendarView = false;
      this.changeView('table');
    } else {
      this.calendarView = true;
      this.tableView = false;
    }

    if (this.tenantType == 'club') {
      this.showTeamFilter = true;
    } else {
      this.showTeamFilter = false;
    }
  }
  // updated() {}
  // destroyed() {}
}
